import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { hideSplash } from '../lib/splash'
import { readReport, reportsAction, reportsSessionState, reportsSignOut, useReport, dropReportCache, type Row } from './api'
import { setConsignorNames, whenShort } from './format'
import { Access } from './Access'
import { Overview } from './screens/Overview'
import { Home } from './screens/Home'
import { Fees } from './screens/Fees'
import { Payee } from './screens/Payee'
import { Statements } from './screens/Statements'
import { Partner } from './screens/Partner'
import { Daily } from './screens/Daily'
import { Health } from './screens/Health'
import { Log } from './screens/Log'
import { Floor } from './screens/Floor'
import { Cost } from './screens/Cost'
import { Owners } from './screens/Owners'
import { Dups } from './screens/Dups'
import { Sold } from './screens/Sold'
import { Changes } from './screens/Changes'
import { ProfitBuy } from './screens/profit/Buy'
import { ProfitPricing } from './screens/profit/Pricing'
import { ProfitMarkdowns } from './screens/profit/Markdowns'
import { ProfitScorecards } from './screens/profit/Scorecards'
import { ProfitSettings } from './screens/profit/Settings'
import { Financials } from './screens/Financials'
import { SalesBook } from './screens/SalesBook'
import { PayoutsBook } from './screens/PayoutsBook'
import { TopClients } from './screens/clients/TopClients'
import { ClientDetail } from './screens/clients/ClientDetail'
import { ClientsCleanup } from './screens/clients/Cleanup'
import { ClientsChannels, ClientsLapsed, ClientsSellers, ClientsUnattached, ClientsSettings } from './screens/clients/ClientsMore'
import './screens/clients/clients.css'
import './reports.css'

/** Reports lives at reports.starchivedesk.store (paths from /) and, for testing, under /reports on the other hosts. */
export const REPORTS_HOST = window.location.hostname.startsWith('reports.')
export const REPORTS_BASE = REPORTS_HOST ? '/' : '/reports'
export const isReportsLocation = () => REPORTS_HOST || window.location.pathname === '/reports' || window.location.pathname.startsWith('/reports/')

// Month picked in the title bar, shared by every monthly screen
const MonthCtx = createContext<{ month: string; setMonth: (m: string) => void; today: string; goLive: string }>({ month: '', setMonth: () => {}, today: '', goLive: '' })
export const useMonth = () => useContext(MonthCtx)

export interface Meta { clients_on?: boolean; today: string; go_live: string; months: string[]; synced_at: string | null; sync_detail: Row | null; code_set_at: string | null; owners: { employee_id: string; name: string }[]; sheet_id: string | null; consignor_names?: Record<string, string> }
const MetaCtx = createContext<{ meta: Meta | null; refreshMeta: () => void }>({ meta: null, refreshMeta: () => {} })
export const useMeta = () => useContext(MetaCtx)

interface RailItem { to: string; label: string; soon?: boolean }
interface RailGroup { key: string; label: string; icon: string; items: RailItem[] }
/** Three groups; a group's sub-tabs show only while it is open (the open group follows the page, or the last click). */
const GROUPS: RailGroup[] = [
  { key: 'accounting', label: 'Accounting', icon: 'M4 5h16v14H4zM4 10h16M9 14h6', items: [
    { to: '/financials', label: 'Financial statements' },
    { to: '/sales', label: 'Sales' },
    { to: '/month', label: 'Month close' },
    { to: '/fees', label: 'Fees and deposits' },
    { to: '/payouts', label: 'Payouts' },
    { to: '/statements', label: 'Consignor statements' },
    { to: '/partner', label: 'Partner settlement' },
    { to: '/daily', label: 'Daily close' },
    { to: '/log', label: 'Sales and purchases log' },
    { to: '/health', label: 'Data health' },
  ] },
  { key: 'inventory', label: 'Inventory', icon: 'M3 8l9-5 9 5v8l-9 5-9-5V8zM12 8v8M3 8l9 5 9-5', items: [
    { to: '/inventory', label: 'On the floor' },
    { to: '/inventory/cost', label: 'Cost' },
    { to: '/inventory/owners', label: 'Owners' },
    { to: '/inventory/duplicates', label: 'Duplicates' },
    { to: '/inventory/sold', label: 'Sold pieces' },
    { to: '/inventory/changes', label: 'Changes' },
  ] },
  // Client accounts (Client Accounts plan, section 3): shown once app_settings 'clients'.on is set (after the customer cleanup)
  { key: 'clients', label: 'Clients', icon: 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21v-1a8 8 0 0 1 16 0v1', items: [
    { to: '/clients', label: 'Top clients' },
    { to: '/clients/channels', label: 'Channels and tenders' },
    { to: '/clients/lapsed', label: 'Lapsed and at risk' },
    { to: '/clients/sellers', label: 'Sellers and consignors' },
    { to: '/clients/unattached', label: 'Unattached sales' },
    { to: '/clients/cleanup', label: 'Clean up' },
    { to: '/clients/settings', label: 'Settings' },
  ] },
  { key: 'profit', label: 'Profit (Shane)', icon: 'M4 19V5M4 19h16M8 15v-4M12 15V7M16 15v-6', items: [
    { to: '/profit', label: 'What to buy more of' },
    { to: '/pricing', label: 'Pricing a piece' },
    { to: '/markdowns', label: 'Markdowns and returns' },
    { to: '/profit/scorecards', label: 'Scorecards' },
    { to: '/profit/settings', label: 'Settings' },
  ] },
]
const groupOf = (pathname: string) => pathname === '/' ? '' : GROUPS.find((g) => g.items.some((r) => r.to === '/' ? pathname === '/' : pathname === r.to || pathname.startsWith(r.to + '/')))?.key ?? 'accounting'

function Icon({ d }: { d: string }) {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
}

function Rail({ onLock }: { onLock: () => void }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { meta, refreshMeta } = useMeta()
  const [syncing, setSyncing] = useState(false)
  const pageGroup = groupOf(pathname)
  // the open group follows the page; a click on a group head overrides that until the page changes
  const [manual, setManual] = useState<{ group: string; at: string } | null>(null)
  const open = manual && manual.at === pathname ? manual.group : pageGroup
  const setOpen = (group: string) => setManual({ group, at: pathname })
  const active = (to: string) => to === '/' ? pathname === '/' : pathname === to || (pathname.startsWith(to + '/') && !GROUPS.some((g) => g.items.some((r) => r.to !== to && r.to.startsWith(to + '/') && (pathname === r.to || pathname.startsWith(r.to + '/')))))
  const clickGroup = (g: RailGroup) => {
    if (open === g.key) { setOpen(''); return }
    setOpen(g.key)
    if (pageGroup !== g.key) navigate(g.items[0].to)
  }
  const syncNow = async () => {
    setSyncing(true)
    try {
      await reportsAction('sync_now')
      // the sync runs in the background; look for the new audit row for up to a minute
      const started = meta?.synced_at ?? null
      for (let i = 0; i < 20; i++) {
        await new Promise((r) => setTimeout(r, 3000))
        refreshMeta()
        const m = await readReport<Meta>('meta').catch(() => null)
        if (m && m.synced_at && m.synced_at !== started) break
      }
      dropReportCache(); refreshMeta()
    } finally { setSyncing(false) }
  }
  return (
    <nav className="rp-rail" aria-label="Reports">
      <div className="rp-rail__brand"><div className="rp-rail__logo"><img src="/brand/star-black.png" alt="Starchive" /></div><div><span className="rp-rail__name">Starchive</span><span className="rp-rail__sub">Reports</span></div></div>
      <Link to="/" className={'rp-rail__home' + (pathname === '/' ? ' is-active' : '')} aria-current={pathname === '/' ? 'page' : undefined}>
        <Icon d="M3 11l9-8 9 8v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-9z" />Home
      </Link>
      {GROUPS.filter((g) => g.key !== 'clients' || meta?.clients_on || pageGroup === 'clients').map((g) => {
        const isOpen = open === g.key
        const isHere = pageGroup === g.key
        return (
          <div key={g.key} className={'rp-rail__section' + (isOpen ? ' is-open' : '')}>
            <button type="button" className={'rp-rail__head' + (isHere ? ' is-here' : '')} onClick={() => clickGroup(g)} aria-expanded={isOpen} aria-controls={'rail-' + g.key}>
              <Icon d={g.icon} />{g.label}
              <svg className="rp-rail__chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
            </button>
            {isOpen && (
              <div className="rp-rail__items" id={'rail-' + g.key}>
                {g.items.map((r) => <Link key={r.to} to={r.to} className={'rp-rail__item' + (active(r.to) ? ' is-active' : '') + (r.soon ? ' is-soon' : '')} aria-current={active(r.to) ? 'page' : undefined}>{r.label}{r.soon && <span className="rp-rail__soon">next</span>}</Link>)}
              </div>
            )}
          </div>
        )
      })}
      <div className="rp-rail__spacer" />
      <div className="rp-rail__foot">
        <div className="rp-rail__sync"><span>Polaris (the ledger sheet) synced</span><b>{meta ? whenShort(meta.synced_at, meta.today) : '…'}</b></div>
        <button type="button" className="rp-rail__btn" onClick={syncNow} disabled={syncing} title="Write tonight's tabs to Polaris now">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5" /></svg>
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
        <button type="button" className="rp-rail__btn rp-rail__btn--quiet" onClick={onLock}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
          Lock Reports
        </button>
      </div>
    </nav>
  )
}

/** Title bar + content column, used by every screen. */
export function Screen({ title, sub, right, children, wide }: { title: string; sub?: ReactNode; right?: ReactNode; children: ReactNode; wide?: boolean }) {
  return (
    <div className="rp-main">
      <header className="rp-title"><div><h1>{title}</h1>{sub && <span>{sub}</span>}</div>{right && <div className="rp-title__right">{right}</div>}</header>
      <div className={'rp-body' + (wide ? ' rp-body--wide' : '')}>{children}</div>
    </div>
  )
}

function Shell({ onLock }: { onLock: () => void }) {
  const meta = useReport<Meta>('meta')
  const today = meta.data?.today ?? new Date().toISOString().slice(0, 10)
  const [month, setMonthState] = useState<string>(() => { try { return localStorage.getItem('starchive.reports.month') || '' } catch { return '' } })
  const setMonth = useCallback((m: string) => { setMonthState(m); try { localStorage.setItem('starchive.reports.month', m) } catch { /* ignore */ } }, [])
  const cur = month && month <= today.slice(0, 7) ? month : today.slice(0, 7)
  const monthValue = useMemo(() => ({ month: cur, setMonth, today, goLive: meta.data?.go_live ?? '2026-09-23' }), [cur, setMonth, today, meta.data?.go_live])
  const metaValue = useMemo(() => ({ meta: meta.data, refreshMeta: meta.refresh }), [meta.data, meta.refresh])
  setConsignorNames(meta.data?.consignor_names)
  return (
    <MetaCtx.Provider value={metaValue}>
      <MonthCtx.Provider value={monthValue}>
        <div className="rp">
          <Rail onLock={onLock} />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/financials" element={<Financials />} />
            <Route path="/sales" element={<SalesBook />} />
            <Route path="/month" element={<Overview />} />
            <Route path="/fees" element={<Fees />} />
            <Route path="/payouts" element={<PayoutsBook />} />
            <Route path="/payouts/bought/:intakeId" element={<PayoutsBook />} />
            <Route path="/payouts/:payee" element={<Payee />} />
            <Route path="/statements" element={<Statements />} />
            <Route path="/statements/:payee" element={<Statements />} />
            <Route path="/partner" element={<Partner />} />
            <Route path="/daily" element={<Daily />} />
            <Route path="/daily/:day" element={<Daily />} />
            <Route path="/log" element={<Log />} />
            <Route path="/health" element={<Health />} />
            <Route path="/inventory" element={<Floor />} />
            <Route path="/inventory/cost" element={<Cost />} />
            <Route path="/inventory/owners" element={<Owners />} />
            <Route path="/inventory/duplicates" element={<Dups />} />
            <Route path="/inventory/sold" element={<Sold />} />
            <Route path="/inventory/changes" element={<Changes />} />
            <Route path="/clients" element={<TopClients />} />
            <Route path="/clients/c/:id" element={<ClientDetail />} />
            <Route path="/clients/channels" element={<ClientsChannels />} />
            <Route path="/clients/lapsed" element={<ClientsLapsed />} />
            <Route path="/clients/sellers" element={<ClientsSellers />} />
            <Route path="/clients/unattached" element={<ClientsUnattached />} />
            <Route path="/clients/cleanup" element={<ClientsCleanup />} />
            <Route path="/clients/settings" element={<ClientsSettings />} />
            <Route path="/profit" element={<ProfitBuy />} />
            <Route path="/profit/scorecards" element={<ProfitScorecards />} />
            <Route path="/profit/settings" element={<ProfitSettings />} />
            <Route path="/pricing" element={<ProfitPricing />} />
            <Route path="/markdowns" element={<ProfitMarkdowns />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </MonthCtx.Provider>
    </MetaCtx.Provider>
  )
}

export default function ReportsApp() {
  const [state, setState] = useState<'checking' | 'none' | 'other' | 'reports'>('checking')
  useEffect(() => {
    reportsSessionState().then(setState)
    const { data: sub } = supabase.auth.onAuthStateChange((e) => { if (e === 'SIGNED_OUT') { dropReportCache(); setState('none') } })
    return () => sub.subscription.unsubscribe()
  }, [])
  useEffect(() => { if (state !== 'checking') hideSplash() }, [state])
  useEffect(() => {
    // the tab: "Starchive Reports" with the black star on a white tile (the counter app keeps its black tile)
    document.title = 'Starchive Reports'
    for (const [rel, href] of [['icon', '/reports-icon.png'], ['apple-touch-icon', '/reports-icon-512.png']] as const) {
      const link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`) ?? Object.assign(document.createElement('link'), { rel })
      link.href = href
      if (!link.parentNode) document.head.appendChild(link)
    }
  }, [])
  const lock = async () => { await reportsSignOut(); dropReportCache(); setState('none') }
  if (state === 'checking') return null
  if (state !== 'reports') return <Access other={state === 'other'} onOpen={() => setState('reports')} />
  return <BrowserRouter basename={REPORTS_BASE}><Shell onLock={lock} /></BrowserRouter>
}
